import type { GardenLayout } from '../types/gardenLayout';

export const MOCK_GARDEN_LAYOUTS: GardenLayout[] = [
  {
    id: '7',
    slug: 'perennial-annual-mix-flower-garden',
    title: 'Perennial & Annual Mix Flower Garden',
    excerpt:
      'A pollinator-packed flower bed that blends come-back-every-year perennials with high-impact annuals — so you get reliable structure and fresh variety each season.',
    date: '2026-06-19',
    videoEmbedUrl: 'https://www.instagram.com/p/DZv0CU4qaqP/embed/',
    videoSourceUrl: 'https://www.instagram.com/p/DZv0CU4qaqP/',
    videoCaption: 'Watch the full layout reveal on Instagram',
    content: `
<h2>The Best of Both Worlds</h2>
<p>Perennials give you a garden that gets better every year — deeper roots, more blooms, less work. Annuals give you flexibility: you can change colors, try new varieties, and fill gaps while your perennials are still getting established. This layout uses both intentionally, putting perennials where you want permanent structure and annuals where you want seasonal excitement.</p>

<h2>Reading the Layout</h2>
<p>The bed is organized with height in mind. Sunflowers anchor the back-right corner as the tallest element, and Bee Balm fills the back-left with its large, airy habit. Everything steps down toward the front, ending with a sprawling carpet of Nasturtium along the bottom edge and a dense border of Marigold and Calendula that acts as both a color statement and a pest deterrent.</p>

<h3>Plant-by-Plant Breakdown</h3>
<ul>
  <li><strong>Back-left:</strong> Bee Balm — a perennial that hummingbirds and bees can't resist. Spreads slowly over years; divide every 3–4 seasons.</li>
  <li><strong>Back-right:</strong> Sunflowers — tall annual, planted in a cluster of three for a natural look rather than a row.</li>
  <li><strong>Upper middle:</strong> Zinnia row — fast-blooming annuals that bridge the tall back plants and the mid-height layer. Cut them constantly to keep blooms coming.</li>
  <li><strong>Mid-left accent:</strong> Cosmos — wispy, self-seeding annual that fills in beautifully around the base of Bee Balm.</li>
  <li><strong>Small accent dots:</strong> Borage — scattered through the middle; edible blue flowers, a magnet for pollinators, and a companion plant that repels tomato hornworm.</li>
  <li><strong>Mid-left:</strong> Coneflower (×2) — reliable perennial that blooms midsummer and feeds goldfinches from the seedheads in fall. Leave them standing through winter.</li>
  <li><strong>Mid-right:</strong> Lavender — a cornerstone perennial. Drought-tolerant once established, beloved by bees, and it smells incredible.</li>
  <li><strong>Front-left:</strong> Nasturtium (×2 patches) — annual that sprawls along the edge. Edible flowers and leaves; draws aphids away from other plants.</li>
  <li><strong>Front-right:</strong> Alternating Marigold and Calendula — a classic pairing. Both deter nematodes and aphids, both are edible, and the orange-yellow palette ties the whole front border together.</li>
</ul>

<h2>Perennial vs. Annual at a Glance</h2>
<p><strong>Perennials in this bed</strong> (come back each year): Bee Balm, Coneflower, Lavender.<br/>
<strong>Annuals</strong> (replant each season): Sunflowers, Zinnia, Cosmos, Borage, Nasturtium, Marigold, Calendula.</p>
<p>Borage and Nasturtium are enthusiastic self-seeders — if you let them drop seed, they'll often come back on their own without replanting.</p>

<h2>Pollinator Value</h2>
<p>Every plant in this garden was chosen with pollinators in mind. Bee Balm, Lavender, and Borage are among the highest-rated plants for native bee forage. Zinnia and Marigold are butterfly favorites. Sunflowers feed bees early in the morning when pollen is richest. Plant this bed and watch the activity — it's remarkable.</p>
    `,
    downloadImageUrl: '/layouts/perennial-annual-mix-thumbnail.png',
    thumbnailUrl: '/layouts/perennial-annual-mix-thumbnail.png',
    dimensions: '5ft × 8ft',
    squareFootage: 40,
    plants: [
      { name: 'Bee Balm', quantity: 1, spacing: '24"', notes: 'Perennial; divide every 3–4 years' },
      { name: 'Sunflower', quantity: 3, spacing: '12"', notes: 'Back-right cluster; annual' },
      { name: 'Zinnia', quantity: 5, spacing: '10"', notes: 'Annual; deadhead constantly for more blooms' },
      { name: 'Cosmos', quantity: 4, spacing: '8"', notes: 'Annual; self-seeds if allowed to drop' },
      { name: 'Borage', quantity: 3, spacing: '8"', notes: 'Annual; edible blue flowers, excellent pollinator plant' },
      { name: 'Coneflower (Echinacea)', quantity: 2, spacing: '18"', notes: 'Perennial; leave seedheads for birds in fall' },
      { name: 'Lavender', quantity: 1, spacing: '24"', notes: 'Perennial; drought tolerant once established' },
      { name: 'Nasturtium', quantity: 6, spacing: '10"', notes: 'Annual; sprawling front-left edge, edible, trap crop' },
      { name: 'Marigold', quantity: 6, spacing: '8"', notes: 'Annual; front border, pest deterrent' },
      { name: 'Calendula', quantity: 6, spacing: '8"', notes: 'Annual; alternates with marigold along front border' },
    ],
    tags: ['flowers', 'pollinators', 'perennials', 'annuals', 'cottage garden', 'summer'],
    difficulty: 'beginner',
    season: 'Summer',
  },
  {
    id: '6',
    slug: 'texture-garden',
    title: 'Texture Garden',
    excerpt:
      'A garden designed entirely around texture — feathery dill, velvety strawflower, crinkled kale, and spiky celosia create a visual feast that looks as good as it grows.',
    date: '2026-06-20',
    videoEmbedUrl: 'https://www.instagram.com/p/DZynkr7qiQO/embed/',
    videoSourceUrl: 'https://www.instagram.com/p/DZynkr7qiQO/',
    videoCaption: 'Watch the full layout reveal on Instagram',
    content: `
<h2>Why Texture?</h2>
<p>Most garden designs lead with color — and color is great. But texture is what makes a garden interesting up close. When you walk past this bed and brush your hand along the feathery dill, or pause to look at the papery layers of a strawflower, that's the experience we designed for.</p>

<h2>The Design</h2>
<p>This layout mixes edibles and ornamentals in the same bed, which is one of our favorite approaches. The back row anchors the design with big, bold globes of strawflower and celosia. The middle layer is productive — rows of carrots and a cluster of kale provide texture and food. The front edge softens everything with low, spreading nasturtium and creeping thyme.</p>

<h3>Layer by Layer</h3>
<ul>
  <li><strong>Back row:</strong> Strawflower (×2) on the outside corners, Celosia in the center — all tall, textural, and long-blooming</li>
  <li><strong>Upper middle:</strong> Two rows of carrots across the width — the ferny tops add soft texture as they grow</li>
  <li><strong>Middle:</strong> Gomphrena, Phlox, and Verbena — compact mounding plants with contrasting leaf shapes</li>
  <li><strong>Lower middle:</strong> Kale (×2) flanking a cluster of chives — the crinkled kale leaves against the upright chive blades is a study in contrast</li>
  <li><strong>Front edge:</strong> Nasturtium sprawling across the full width, with thyme tucked into the corner</li>
  <li><strong>Accent dots:</strong> Small strawflower starts scattered between plants for pops of color at ground level</li>
</ul>

<h2>Companion Planting Notes</h2>
<p>Nasturtium at the front edge does double duty: it's edible (flowers and leaves taste peppery in salads) and it acts as a trap crop, drawing aphids away from your kale. Dill in the upper right attracts beneficial wasps and hoverflies that prey on common garden pests.</p>

<h2>Harvest Notes</h2>
<p>Pull carrots at 70–80 days. Harvest kale leaves from the outside in, starting at 50–60 days. Snip chives as needed — they regrow quickly. Strawflower and celosia can be cut for dried arrangements; the more you cut, the more they bloom.</p>
    `,
    downloadImageUrl: '/layouts/texture-garden-thumbnail.png',
    thumbnailUrl: '/layouts/texture-garden-thumbnail.png',
    dimensions: '4ft × 6ft',
    squareFootage: 24,
    plants: [
      { name: 'Strawflower', quantity: 2, spacing: '12"', notes: 'Back corners; cut for dried arrangements' },
      { name: 'Celosia', quantity: 1, spacing: '12"', notes: 'Back center' },
      { name: 'Carrots', quantity: 20, spacing: '3"', notes: 'Two rows across the upper middle; direct sow' },
      { name: 'Dill', quantity: 3, spacing: '10"', notes: 'Upper right cluster; attracts beneficial insects' },
      { name: 'Gomphrena', quantity: 1, spacing: '10"' },
      { name: 'Phlox', quantity: 1, spacing: '10"' },
      { name: 'Verbena', quantity: 1, spacing: '10"' },
      { name: 'Kale', quantity: 2, spacing: '12"', notes: 'Harvest outer leaves continuously' },
      { name: 'Chives', quantity: 1, spacing: '10"', notes: 'Perennial; snip as needed' },
      { name: 'Nasturtium', quantity: 6, spacing: '10"', notes: 'Front edge; edible flowers, trap crop for aphids' },
      { name: 'Thyme', quantity: 1, spacing: '10"', notes: 'Front corner; perennial groundcover' },
    ],
    tags: ['texture', 'ornamental edible', 'flowers', 'kale', 'summer'],
    difficulty: 'beginner',
    season: 'Summer',
  },
  {
    id: '1',
    slug: 'classic-spring-raised-bed',
    title: 'Classic 4×8 Spring Raised Bed',
    excerpt:
      'A beginner-friendly raised bed packed with cool-season favorites — lettuce, spinach, kale, and radishes — designed to maximize every square foot.',
    date: '2026-06-17',
    videoEmbedUrl: 'https://www.youtube.com/embed/tJUcqmS1_wY',
    videoSourceUrl: 'https://www.tiktok.com/@bloomagrows',
    videoCaption: 'Watch the full layout walkthrough on our TikTok',
    content: `
<h2>Why This Layout Works</h2>
<p>Cool-season crops thrive in the shoulder seasons — that sweet spot before summer heat pushes them to bolt. This 4×8 bed is divided into four 2×4 sections, each dedicated to a different plant family so you can easily rotate crops year after year.</p>

<h2>The Design Principles</h2>
<p>We placed taller crops (kale) on the north end so they never shadow their shorter neighbors. Radishes and lettuce fill in quickly, giving you harvests within 30–45 days while your kale and spinach mature. The dense planting also helps suppress weeds — a bonus for any new gardener.</p>

<h3>Section Breakdown</h3>
<ul>
  <li><strong>North section (2×4):</strong> Kale — 4 plants in a 2×2 grid, 18" apart</li>
  <li><strong>Second section (2×4):</strong> Spinach — direct sow in 3 rows, 6" between plants</li>
  <li><strong>Third section (2×4):</strong> Head lettuce mix — 8 plants, 10" apart</li>
  <li><strong>South section (2×4):</strong> Radishes — direct sow densely, thin to 3" apart</li>
</ul>

<h2>Soil & Setup</h2>
<p>We recommend filling your raised bed with a 60/30/10 mix: 60% topsoil, 30% compost, and 10% perlite or coarse sand for drainage. This gives cool-season crops the moisture retention they love without risking root rot.</p>

<h2>When to Plant</h2>
<p>This layout shines in two windows: early spring (4–6 weeks before your last frost date) and early fall (8–10 weeks before your first frost date). Both seasons give you a full harvest before extreme temperatures arrive.</p>

<h2>Succession Planting Tip</h2>
<p>As radishes finish (around week 5–6), replant that section with a second round of lettuce for a continuous harvest through the season. This keeps every square foot productive all season long.</p>
    `,
    downloadImageUrl: 'https://images.unsplash.com/photo-1585320806297-9a577b4c86c6?w=1200&auto=format&fit=crop',
    thumbnailUrl: 'https://images.unsplash.com/photo-1585320806297-9a577b4c86c6?w=800&auto=format&fit=crop',
    dimensions: '4ft × 8ft',
    squareFootage: 32,
    plants: [
      { name: 'Kale', quantity: 4, spacing: '18"', notes: 'Place on north end to avoid shading' },
      { name: 'Spinach', quantity: 24, spacing: '6"', notes: 'Direct sow in rows' },
      { name: 'Lettuce Mix', quantity: 8, spacing: '10"' },
      { name: 'Radishes', quantity: 48, spacing: '3"', notes: 'Thin after germination' },
    ],
    tags: ['raised bed', 'cool season', 'beginner', 'spring', 'fall'],
    difficulty: 'beginner',
    season: 'Spring / Fall',
  },
  {
    id: '2',
    slug: 'summer-salsa-garden',
    title: 'Summer Salsa Garden — 4×4',
    excerpt:
      'Everything you need for fresh homemade salsa, all in one 4×4 raised bed. Tomatoes, peppers, onions, and cilantro grown together in perfect harmony.',
    date: '2026-06-17',
    videoEmbedUrl: 'https://www.youtube.com/embed/3L__3KzAHa8',
    videoSourceUrl: 'https://www.instagram.com/bloomagrows',
    videoCaption: 'See the full build on our Instagram Reels',
    content: `
<h2>From Garden to Salsa Bowl</h2>
<p>This layout was designed with one goal: everything for a fresh batch of salsa, growing right outside your door. In just 16 square feet, you can grow enough tomatoes, peppers, onions, and cilantro to keep your kitchen supplied from midsummer through early fall.</p>

<h2>Companion Planting at Work</h2>
<p>The plants in this layout aren't just coexisting — they're actively helping each other. Basil (squeezed in at the corners) is a well-known companion for tomatoes, said to improve flavor and deter aphids. Cilantro attracts beneficial insects like parasitic wasps that prey on common garden pests.</p>

<h3>The Layout</h3>
<ul>
  <li><strong>Center back:</strong> 1 indeterminate tomato (caged or staked, 24" from edges)</li>
  <li><strong>Front corners:</strong> 2 pepper plants, 12" from corners</li>
  <li><strong>Front center:</strong> 4 green onions, 4" apart</li>
  <li><strong>Side borders:</strong> Cilantro sown directly, 3–4" between plants</li>
  <li><strong>Corner pockets:</strong> 4 basil transplants</li>
</ul>

<h2>Staking Strategy</h2>
<p>The indeterminate tomato in the center is the tallest element and needs support from day one. Install a cage or t-post before the plant gets established so you avoid disturbing roots later. The peppers are self-supporting until they load up with fruit — tie them loosely to a short stake at that point.</p>

<h2>Watering Notes</h2>
<p>Tomatoes are water-sensitive: inconsistent watering is the #1 cause of blossom end rot and cracking. Aim for 1–2 inches per week, delivered at soil level (drip or soaker hose) to keep foliage dry and reduce disease pressure. Peppers tolerate slightly drier conditions once established.</p>

<h2>Harvest Window</h2>
<p>Start harvesting cilantro leaves within 3–4 weeks of planting. Green onions are ready in 60 days. Peppers take 70–90 days to mature, and your tomatoes will begin fruiting 65–85 days after transplanting depending on the variety.</p>
    `,
    downloadImageUrl: 'https://images.unsplash.com/photo-1416879595100-ee5571d9e6c0?w=1200&auto=format&fit=crop',
    thumbnailUrl: 'https://images.unsplash.com/photo-1416879595100-ee5571d9e6c0?w=800&auto=format&fit=crop',
    dimensions: '4ft × 4ft',
    squareFootage: 16,
    plants: [
      { name: 'Tomato (indeterminate)', quantity: 1, spacing: '24"', notes: 'Cage or stake immediately' },
      { name: 'Bell Pepper', quantity: 2, spacing: '12"' },
      { name: 'Green Onion', quantity: 4, spacing: '4"' },
      { name: 'Cilantro', quantity: 12, spacing: '3–4"', notes: 'Direct sow along borders' },
      { name: 'Basil', quantity: 4, spacing: '8"', notes: 'Corner pockets' },
    ],
    tags: ['raised bed', 'summer', 'tomatoes', 'herbs', 'companion planting'],
    difficulty: 'beginner',
    season: 'Summer',
  },
  {
    id: '3',
    slug: 'three-sisters-companion-planting',
    title: 'Three Sisters — 6×6 Companion Planting',
    excerpt:
      'The oldest garden layout in North America. Corn, beans, and squash grown together in a method refined by Indigenous farmers over thousands of years.',
    date: '2026-06-13',
    videoEmbedUrl: 'https://www.youtube.com/embed/vOkMEFJ1M-I',
    videoSourceUrl: 'https://www.tiktok.com/@bloomagrows',
    videoCaption: 'The three sisters in action — watch on TikTok',
    content: `
<h2>Ancient Wisdom, Modern Garden</h2>
<p>The Three Sisters is one of the most elegant growing systems ever devised. Developed by Indigenous peoples of North America, this polyculture uses corn, beans, and squash as a mutually beneficial trio — each plant doing something the others cannot.</p>

<h2>How the System Works</h2>
<p>Corn provides the vertical structure — a living trellis for pole beans to climb. Beans are nitrogen-fixers, pulling nitrogen from the air and depositing it into the soil, feeding the heavy-feeding corn and squash. Squash spreads along the ground with large leaves that shade the soil, locking in moisture and crowding out weeds.</p>

<h2>Layout Instructions</h2>
<p>This layout works best in a 6×6 space, though it can scale up. Here's how to arrange the plantings:</p>
<ol>
  <li><strong>Week 1:</strong> Plant 4 corn seeds in a 2×2 cluster at the center, 12" apart. Do NOT plant beans yet — corn must have a head start.</li>
  <li><strong>Week 3:</strong> Once corn is 4–6" tall, plant 2 bean seeds at the base of each corn stalk (8 beans total).</li>
  <li><strong>Week 3:</strong> Plant 2–3 squash seeds in each corner of the bed (you'll thin to 1 plant per corner).</li>
</ol>

<h2>Why the Timing Matters</h2>
<p>Planting everything at once is a common mistake. Beans planted at the same time as corn will try to climb before the corn is strong enough to support them, tangling the young stalks. The 2–3 week delay makes all the difference.</p>

<h2>Variety Selection</h2>
<p>For corn, choose a sweet corn or flour corn with strong stalks — not popcorn varieties, which are typically too short. For beans, any pole bean works beautifully; Rattlesnake and Kentucky Wonder are classics. For squash, a spreading summer squash or small pumpkin keeps things manageable in a 6×6 space.</p>

<h2>What to Expect</h2>
<p>This is not a high-yield-per-square-foot system — it's a system of relationships. The payoff is in soil health, reduced watering, and the quiet satisfaction of growing the way humans have grown for millennia.</p>
    `,
    downloadImageUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&auto=format&fit=crop',
    thumbnailUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&auto=format&fit=crop',
    dimensions: '6ft × 6ft',
    squareFootage: 36,
    plants: [
      { name: 'Sweet Corn', quantity: 4, spacing: '12"', notes: 'Plant first, wait 2–3 weeks' },
      { name: 'Pole Beans', quantity: 8, spacing: '6"', notes: '2 beans at base of each corn stalk' },
      { name: 'Summer Squash', quantity: 4, spacing: '24"', notes: 'One per corner, thin to strongest seedling' },
    ],
    tags: ['companion planting', 'summer', 'indigenous', 'corn', 'beans', 'squash'],
    difficulty: 'intermediate',
    season: 'Summer',
  },
  {
    id: '4',
    slug: 'compact-kitchen-herb-garden',
    title: 'Compact Kitchen Herb Garden — 2×6',
    excerpt:
      'A narrow 2×6 bed designed to live right outside your kitchen door. Eight essential culinary herbs, organized so the most-used ones are always within reach.',
    date: '2026-06-10',
    videoEmbedUrl: 'https://www.youtube.com/embed/rJZ3GNulJMg',
    videoSourceUrl: 'https://www.instagram.com/bloomagrows',
    videoCaption: 'See how we planted this kitchen herb bed on Instagram',
    content: `
<h2>The Herb Garden You'll Actually Use</h2>
<p>Most herb gardens fail not because of bad soil or bad luck — they fail because they're inconvenient. This layout is built to solve that problem. A narrow 2×6 bed fits along any path, fence, or kitchen wall, and every plant is reachable without stepping into the bed.</p>

<h2>The Organization Logic</h2>
<p>We arranged this bed in order of harvest frequency, placing the most-grabbed herbs closest to the path. Basil and parsley are at the front because you'll pinch them almost daily in summer. Rosemary and thyme are at the back — they're perennials that need little attention once established.</p>

<h3>Planting Map (front to back, left to right)</h3>
<ul>
  <li><strong>Front-left:</strong> Basil (replant annually, harvest often)</li>
  <li><strong>Front-right:</strong> Flat-leaf parsley (biennial, treat as annual)</li>
  <li><strong>Middle-left:</strong> Chives (perennial, self-dividing)</li>
  <li><strong>Middle-center:</strong> Cilantro (succession sow every 3 weeks)</li>
  <li><strong>Middle-right:</strong> Dill (let some go to seed for next year)</li>
  <li><strong>Back-left:</strong> Rosemary (perennial, keep trimmed)</li>
  <li><strong>Back-center:</strong> Sage (perennial, low water once established)</li>
  <li><strong>Back-right:</strong> Thyme (perennial, groundcover habit)</li>
</ul>

<h2>Perennial vs. Annual Planning</h2>
<p>This layout combines perennial herbs (rosemary, sage, thyme, chives) with annual ones (basil, cilantro, dill, parsley). Every spring, you only need to replant the annuals in the front half — the perennials come back on their own, growing a little larger each year.</p>

<h2>The Mint Exception</h2>
<p>You'll notice mint is not in this layout. That's intentional. Mint is a vigorous spreader that will take over any bed it's planted in. If you want mint, grow it in a separate container sunk into the ground — the pot walls keep the roots contained.</p>

<h2>Watering & Feeding</h2>
<p>Most culinary herbs prefer drier conditions than vegetables. Water deeply but infrequently — let the top inch of soil dry out between waterings. Avoid high-nitrogen fertilizers, which push leafy growth at the expense of the essential oils that make herbs flavorful.</p>
    `,
    downloadImageUrl: 'https://images.unsplash.com/photo-1542838132-8c7b63a1b985?w=1200&auto=format&fit=crop',
    thumbnailUrl: 'https://images.unsplash.com/photo-1542838132-8c7b63a1b985?w=800&auto=format&fit=crop',
    dimensions: '2ft × 6ft',
    squareFootage: 12,
    plants: [
      { name: 'Basil', quantity: 2, spacing: '10"', notes: 'Annual, harvest frequently to prevent bolting' },
      { name: 'Flat-leaf Parsley', quantity: 2, spacing: '10"' },
      { name: 'Chives', quantity: 1, spacing: '12"', notes: 'Perennial, will spread' },
      { name: 'Cilantro', quantity: 4, spacing: '6"', notes: 'Succession sow every 3 weeks' },
      { name: 'Dill', quantity: 2, spacing: '10"', notes: 'Allow some to go to seed' },
      { name: 'Rosemary', quantity: 1, spacing: '18"', notes: 'Perennial, drought tolerant once established' },
      { name: 'Sage', quantity: 1, spacing: '18"', notes: 'Perennial' },
      { name: 'Thyme', quantity: 1, spacing: '12"', notes: 'Perennial, low-growing' },
    ],
    tags: ['herbs', 'kitchen garden', 'perennials', 'beginner', 'small space'],
    difficulty: 'beginner',
    season: 'Spring through Fall',
  },
  {
    id: '5',
    slug: 'fall-greens-production-bed',
    title: 'Fall Greens Production Bed — 4×8',
    excerpt:
      'Maximize your fall harvest with this 4×8 layout designed for back-to-back plantings of kale, broccoli, bok choy, and Asian greens through first frost.',
    date: '2026-06-06',
    videoEmbedUrl: 'https://www.youtube.com/embed/K4TOrB7at0Y',
    videoSourceUrl: 'https://www.tiktok.com/@bloomagrows',
    videoCaption: 'Fall garden walkthrough on TikTok',
    content: `
<h2>Fall is the Secret Season</h2>
<p>Most gardeners wind down in August, but experienced growers know that fall is one of the most productive seasons of the year. Cool temperatures concentrate sugars in leaves, frost sweetens kale, and pest pressure drops dramatically as summer ends. This layout is built to take full advantage of that window.</p>

<h2>Timing is Everything</h2>
<p>The key to a successful fall bed is counting backwards from your first frost date. Most of the plants in this layout need 45–70 days to mature, so you need to be in the ground by late July or early August in most growing zones. Start seeds indoors in mid-July to get transplants ready in time.</p>

<h2>The Layout</h2>
<p>This 4×8 bed is divided into three zones based on plant height and harvest timing:</p>

<h3>North Zone — Long Season (4×3)</h3>
<ul>
  <li>4 broccoli transplants, 18" apart in a 2×2 grid</li>
  <li>These will be your last harvest, maturing 60–80 days after transplanting</li>
</ul>

<h3>Middle Zone — Medium Season (4×3)</h3>
<ul>
  <li>6 lacinato kale transplants, 12" apart</li>
  <li>Begin harvesting outer leaves at 50–60 days; continue until hard freeze</li>
</ul>

<h3>South Zone — Quick Crop (4×2)</h3>
<ul>
  <li>2 rows of bok choy (4 plants per row), 8" apart</li>
  <li>Ready in 45–50 days; harvest entire head for a one-time flush</li>
  <li>Replant with arugula or spinach after harvest for a second flush</li>
</ul>

<h2>Row Cover as Insurance</h2>
<p>A single layer of floating row cover (Agribon AG-19 or similar) can extend your season 4–6 weeks past your first frost date. Install wire hoops when you plant and keep the row cover handy — you can deploy it in minutes when a freeze warning comes through.</p>

<h2>Why Broccoli in the Back</h2>
<p>Broccoli is the tallest plant in this bed, and placing it on the north end means it won't cast shade over its shorter companions as the sun angle drops in fall. This is especially important in the shorter days of September and October when maximizing light matters most.</p>
    `,
    downloadImageUrl: 'https://images.unsplash.com/photo-1566207474742-de921626ad0c?w=1200&auto=format&fit=crop',
    thumbnailUrl: 'https://images.unsplash.com/photo-1566207474742-de921626ad0c?w=800&auto=format&fit=crop',
    dimensions: '4ft × 8ft',
    squareFootage: 32,
    plants: [
      { name: 'Broccoli', quantity: 4, spacing: '18"', notes: 'North zone, plant last; matures in 60–80 days' },
      { name: 'Lacinato Kale', quantity: 6, spacing: '12"', notes: 'Middle zone; harvest outer leaves continuously' },
      { name: 'Bok Choy', quantity: 8, spacing: '8"', notes: 'South zone; harvest whole head at 45–50 days' },
      { name: 'Arugula', quantity: 20, spacing: '4"', notes: 'Succession sow in south zone after bok choy harvest' },
    ],
    tags: ['raised bed', 'fall', 'cool season', 'greens', 'brassicas', 'succession planting'],
    difficulty: 'intermediate',
    season: 'Fall',
  },
];
